import { RPCDocs } from "../../services/rpc.js";
// src/modules/Notifications/preferences-utils.ts
var Days = RPCDocs.Notifications.GetMyNotificationPreferences.v1.Days;
var Email = RPCDocs.Notifications.GetMyNotificationPreferences.v1.Email;
var OffDuty = RPCDocs.Notifications.GetMyNotificationPreferences.v1.OffDuty;
/** Opt-out model: a missing entry or missing channel key means ENABLED. */
function isChannelEnabled(prefs, category, channel) {
	return (prefs?.find((pref) => pref.category === category))?.[channel] !== false;
}
/**
* Returns the FULL categoryChannelPrefs array with one channel toggled —
* set-my-notification-preferences replaces the whole field, so partial
* arrays would drop other categories' choices.
*/
function toggleCategoryChannel(prefs, category, channel, enabled) {
	const existing = prefs?.find((pref) => pref.category === category);
	return [...(prefs ?? []).filter((pref) => pref.category !== category), {
		...existing ?? { category },
		[channel]: enabled
	}];
}
/** Overnight windows (end < start, e.g. 22:00 → 07:00) are valid per contract. */
function isOvernightWindow(window) {
	return window.end < window.start;
}
var DAY_LABELS = {
	[Days.Fri]: "Fri",
	[Days.Mon]: "Mon",
	[Days.Sat]: "Sat",
	[Days.Sun]: "Sun",
	[Days.Thu]: "Thu",
	[Days.Tue]: "Tue",
	[Days.Wed]: "Wed"
};
function formatWindowDays(days) {
	if (!days?.length) return "Every day";
	return days.map((day) => DAY_LABELS[day]).join(", ");
}
function isValidWindowTime(value) {
	return /^([01]\d|2[0-3]):[0-5]\d$/.test(value);
}
/** Atalaya TimePicker speaks ISO timestamps; quiet-hours windows are HH:mm. */
function hhmmToISO(hhmm) {
	const [hour, minute] = hhmm.split(":").map(Number);
	const date = /* @__PURE__ */ new Date();
	date.setHours(hour, minute, 0, 0);
	return date.toISOString();
}
function isoToHHmm(iso) {
	const date = new Date(iso);
	return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}
var WEEK_KEY_TO_DAY = [
	["sunday", Days.Sun],
	["monday", Days.Mon],
	["tuesday", Days.Tue],
	["wednesday", Days.Wed],
	["thursday", Days.Thu],
	["friday", Days.Fri],
	["saturday", Days.Sat]
];
/** Omitted/empty days = every day = all true. */
function daysToWeek(days) {
	const everyDay = !days?.length;
	return WEEK_KEY_TO_DAY.reduce((week, [key, day]) => {
		week[key] = everyDay || !!days?.includes(day);
		return week;
	}, {});
}
/** All seven selected → undefined (contract: omitted = every day). */
function weekToDays(week) {
	const days = WEEK_KEY_TO_DAY.filter(([key]) => week[key]).map(([, day]) => day);
	return days.length === 7 ? void 0 : days;
}
/**
* System defaults for the three-tier chain (user → org → system).
* The single place these values exist in the frontend.
*/
var SYSTEM_CHANNEL_POLICY_DEFAULTS = {
	email: Email.Always,
	push: OffDuty.QuietHours,
	sms: OffDuty.QuietHours
};
/** The raw value of one leaf, or undefined when the leaf is absent (= inheriting). */
function readChannelPolicyLeaf(policies, leaf) {
	if (!policies) return void 0;
	return leaf === "email" ? policies.email : policies[leaf]?.offDuty;
}
/**
* Presence-based resolution: an explicitly set leaf wins even when its value
* equals a default; an absent leaf inherits org default, then system default.
*/
function resolveChannelPolicy(leaf, userPolicies, orgPolicies) {
	const userValue = readChannelPolicyLeaf(userPolicies, leaf);
	if (userValue !== void 0) return {
		source: "user",
		value: userValue
	};
	const orgValue = readChannelPolicyLeaf(orgPolicies, leaf);
	if (orgValue !== void 0) return {
		source: "org",
		value: orgValue
	};
	return {
		source: "system",
		value: SYSTEM_CHANNEL_POLICY_DEFAULTS[leaf]
	};
}
/**
* Returns a NEW raw channelPolicies object with one leaf explicitly set.
* Contract: set-my-notification-preferences channelPolicies is a whole-object
* replace — the result must contain every leaf the user has ever chosen, and
* nothing else. Screens must never build these objects themselves.
*/
function setChannelPolicyLeaf(policies, leaf, value) {
	if (leaf === "email") return {
		...policies,
		email: value
	};
	return {
		...policies,
		[leaf]: { offDuty: value }
	};
}
/**
* "Reset to default" = REMOVE the leaf (the user un-chooses; inheritance
* resumes). Never write the default's current value — that would pin it.
* Removing the last leaf yields {} — a legal whole-object replace.
*/
function removeChannelPolicyLeaf(policies, leaf) {
	const next = { ...policies };
	delete next[leaf];
	return next;
}
/** Shared option lists — the single source of policy copy for all screens. */
var EMAIL_POLICY_OPTIONS = [
	{
		label: "Always deliver",
		value: Email.Always
	},
	{
		label: "Defer during quiet hours",
		value: Email.QuietHours
	},
	{
		label: "Never",
		value: Email.Never
	}
];
var OFF_DUTY_POLICY_OPTIONS = [{
	label: "Quiet hours decide",
	value: OffDuty.QuietHours
}, {
	label: "Only notify me when I'm on duty",
	value: OffDuty.Never
}];
/**
* Sentinel option value for "no explicit choice — inherit the default".
* Selecting it removes the leaf. Never a valid policy enum value.
*/
var CHANNEL_POLICY_INHERIT_VALUE = "inherit";
function formatChannelPolicyValue(leaf, value) {
	return (leaf === "email" ? EMAIL_POLICY_OPTIONS : OFF_DUTY_POLICY_OPTIONS).find((option) => option.value === value)?.label ?? value;
}
/**
* Label for a policy select's inherit option, e.g.
* "Default — Organization: Only notify me when I'm on duty".
* Resolves with NO user policies: it shows what the user would get by
* un-choosing, regardless of their current explicit value.
*/
function formatInheritOptionLabel(leaf, orgPolicies) {
	const inherited = resolveChannelPolicy(leaf, void 0, orgPolicies);
	return `Default — ${inherited.source === "org" ? "Organization" : "System"}: ${formatChannelPolicyValue(leaf, inherited.value)}`;
}
export { CHANNEL_POLICY_INHERIT_VALUE, EMAIL_POLICY_OPTIONS, OFF_DUTY_POLICY_OPTIONS, daysToWeek, formatChannelPolicyValue, formatInheritOptionLabel, formatWindowDays, hhmmToISO, isChannelEnabled, isOvernightWindow, isValidWindowTime, isoToHHmm, readChannelPolicyLeaf, removeChannelPolicyLeaf, resolveChannelPolicy, setChannelPolicyLeaf, toggleCategoryChannel, weekToDays };

//# sourceMappingURL=preferences-utils.js.map